import { AppRightMenu } from '@/layout/components/app.rightmenu';
import { AppSearch } from '@/layout/components/app.search';
import { LayoutService } from '@/layout/service/layout.service';
import { SelfRegistrationBannerComponent } from './self-registration-banner/self-registration-banner.component';
import { MovideskChatService } from '@/core/services/movidesk-chat/movidesk-chat.service';
import { MeProfile } from '@/core/services/profile/me-profile.model';
import { HomeState } from '@/modules/home/core/state/home.state';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Store } from '@ngxs/store';
import { filter, Subscription } from 'rxjs';
import { AppConfiguratorComponent } from './app.configurator.component';
import { AppSidebar } from './app.sidebar';
import { AppTopbar } from './app.topbar';
import { AppAccessSidebarComponent } from './app-access-sidebar/app-access-sidebar.component';
import { isFullHeightContentUrl } from './full-height-content';
import { MenuStateService } from '../service/menu-state.service';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    AppTopbar,
    AppSidebar,
    RouterModule,
    AppConfiguratorComponent,
    AppSearch,
    AppRightMenu,
    AppAccessSidebarComponent,
    SelfRegistrationBannerComponent,
  ],
  template: `
    <div class="layout-wrapper" [ngClass]="containerClass()">
      @if (!isLogin) {
        <div app-sidebar></div>
        <app-access-sidebar [items]="menuStateService.sidebarItems()" />
      }
      <div
        class="layout-content-wrapper"
        [class.layout-public]="isLogin"
        [class.layout-content-fill]="isFullHeightContent"
      >
        <div class="layout-content-wrapper-inside">
          @if (!isLogin) {
            @if (!hideTopbar) {
              <div app-topbar></div>
            }
            <cam-self-registration-banner />
          }
          <div class="layout-content" [class.layout-public]="isLogin">
            <!--
            <div app-breadcrumb></div>
            -->
            <router-outlet></router-outlet>
          </div>
          <!--
          <div app-footer></div>
          -->
        </div>
      </div>
      <app-configurator />
      <div app-search></div>
      <div app-rightmenu></div>
      <div class="layout-mask animate-fadein"></div>
    </div>
  `,
})
export class AppLayout implements OnDestroy {
  overlayMenuOpenSubscription: Subscription;
  menuOutsideClickListener: any;
  menuScrollListener: any;

  public menuStateService = inject(MenuStateService);
  private readonly store = inject(Store);
  private readonly movideskChat = inject(MovideskChatService);
  private readonly me = this.store.selectSignal<MeProfile>(HomeState.getMe);

  @ViewChild(AppSidebar) appSidebar!: AppSidebar;
  @ViewChild(AppTopbar) appTopBar!: AppTopbar;

  constructor(
    public layoutService: LayoutService,
    public renderer: Renderer2,
    public router: Router,
  ) {
    effect(() => {
      const meUser = this.me();
      if (meUser) {
        this.movideskChat.initialize(meUser);
      } else {
        this.movideskChat.destroy();
      }
    });
    this.overlayMenuOpenSubscription =
      this.layoutService.overlayOpen$.subscribe(() => {
        if (!this.menuOutsideClickListener) {
          this.menuOutsideClickListener = this.renderer.listen(
            'document',
            'click',
            (event) => this.outsideClickListener(event),
          );
        }
        if (
          (this.layoutService.isHorizontal() ||
            this.layoutService.isSlim() ||
            this.layoutService.isCompact()) &&
          !this.menuScrollListener
        ) {
          this.menuScrollListener = this.renderer.listen(
            this.appSidebar.menuContainer.nativeElement,
            'scroll',
            (event) => {
              if (this.layoutService.isDesktop()) {
                this.hideMenu();
              }
            },
          );
        }
        if (this.layoutService.layoutState().staticMenuMobileActive) {
          this.blockBodyScroll();
        }
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.hideMenu();

        if (this.isLogin) {
          this.movideskChat.destroy();
        }

        const container = document.querySelector(
          '.layout-content-wrapper',
        ) as HTMLElement;
        if (!container) return;

        const fragment = this.router.url.split('#')[1];

        if (fragment) {
          setTimeout(() => {
            const target = document.getElementById(fragment);
            if (target) {
              const targetRect = target.getBoundingClientRect();
              const containerRect = container.getBoundingClientRect();
              container.scrollTop += targetRect.top - containerRect.top;
            }
          }, 150);
        } else {
          container.scrollTop = 0;
        }
      });
  }

  get isFullHeightContent(): boolean {
    return isFullHeightContentUrl(this.router.url);
  }

  get hideTopbar(): boolean {
    return this.router.url.includes('mosaic-view');
  }

  outsideClickListener(event: any) {
    if (this.isOutsideClicked(event)) {
      this.hideMenu();
    }
  }

  isOutsideClicked(event: any) {
    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarButtonEl = document.querySelector('.topbar-left > a');

    return !(
      sidebarEl?.isSameNode(event.target) ||
      sidebarEl?.contains(event.target) ||
      topbarButtonEl?.isSameNode(event.target) ||
      topbarButtonEl?.contains(event.target)
    );
  }

  hideMenu() {
    this.layoutService.layoutState.update((prev) => ({
      ...prev,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
    }));
    if (this.layoutService.isHorizontal()) {
      this.layoutService.onMenuStateChange({ key: '' });
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }

    if (this.menuScrollListener) {
      this.menuScrollListener();
      this.menuScrollListener = null;
    }

    this.unblockBodyScroll();
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp(
          '(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)',
          'gi',
        ),
        ' ',
      );
    }
  }

  containerClass = computed(() => {
    const layoutConfig = this.layoutService.layoutConfig();
    const layoutState = this.layoutService.layoutState();

    return {
      'layout-overlay': layoutConfig.menuMode === 'overlay',
      'layout-static': layoutConfig.menuMode === 'static',
      'layout-slim': layoutConfig.menuMode === 'slim',
      'layout-horizontal': layoutConfig.menuMode === 'horizontal',
      'layout-compact': layoutConfig.menuMode === 'compact',
      'layout-reveal': layoutConfig.menuMode === 'reveal',
      'layout-drawer': layoutConfig.menuMode === 'drawer',
      'layout-overlay-active':
        layoutState.overlayMenuActive || layoutState.staticMenuMobileActive,
      'layout-mobile-active': layoutState.staticMenuMobileActive,
      'layout-static-inactive':
        layoutState.staticMenuDesktopInactive &&
        layoutConfig.menuMode === 'static',
      'layout-sidebar-active': layoutState.sidebarActive,
      'layout-sidebar-anchored': layoutState.anchored,
    };
  });

  ngOnDestroy() {
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }

    this.movideskChat.destroy();
  }

  get isLogin(): boolean {
    const urls = [
      '/login',
      '/forgot-password',
      '/reset-password',
      '/confirm-email',
      '/confirm-login',
    ];
    return urls.some((url) => this.router.url.includes(url));
  }
}
